import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BrainIcon, PencilIcon, XIcon } from 'lucide-react';
import { ConfirmDialog, EmptyState, ErrorNotice } from '@bcecloud/portal-ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { errorMessage, getJson, sendJson } from '@/lib/api';

/**
 * The assistant's memory, made visible: the staff member sees every file
 * BESTie keeps about working with them, and can edit or remove any of it.
 * Transparency is the point — memory the owner cannot inspect is a liability,
 * not a feature (see apps/api/src/api/v3/assistant/CLAUDE.md).
 */

/** One memory file as the index lists it. */
interface MemoryIndexEntry {
  readonly path: string;
  readonly description: string;
  readonly version: number;
  readonly updatedAt: string | null;
}

/** One memory file in full, as the editor loads it. */
interface MemoryFile extends MemoryIndexEntry {
  readonly content: string;
}

/** "Mar 4 · 2:15 PM", or null without a timestamp. */
function whenLabel(updatedAt: string | null): string | null {
  if (updatedAt === null) return null;
  const date = new Date(updatedAt);
  return (
    date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' · ' +
    date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  );
}

/** The state of the editor: closed, or open on a loaded file. */
interface EditorState {
  readonly file: MemoryFile;
  readonly description: string;
  readonly content: string;
}

export function MemoryView(): React.ReactElement {
  const queryClient = useQueryClient();
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [deleting, setDeleting] = useState<MemoryIndexEntry | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const memories = useQuery({
    queryKey: ['assistant-memories'],
    queryFn: ({ signal }) => getJson<MemoryIndexEntry[]>('/assistant/memories', undefined, signal),
  });

  const save = useMutation({
    mutationFn: (state: EditorState) =>
      sendJson('put', '/assistant/memories/file', {
        path: state.file.path,
        content: state.content,
        description: state.description,
        expectedVersion: state.file.version,
      }),
    onSuccess: async () => {
      setEditor(null);
      setNotice(null);
      await queryClient.invalidateQueries({ queryKey: ['assistant-memories'] });
    },
    onError: (error) => setNotice(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (path: string) =>
      sendJson('delete', `/assistant/memories/file?path=${encodeURIComponent(path)}`),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['assistant-memories'] });
    },
    onError: (error) => {
      setDeleting(null);
      setNotice(errorMessage(error));
    },
  });

  /** Loads the full file and opens the editor on it. */
  async function openEditor(entry: MemoryIndexEntry): Promise<void> {
    try {
      const file = await getJson<MemoryFile>('/assistant/memories/file', { path: entry.path });
      setNotice(null);
      setEditor({ file, description: file.description, content: file.content });
    } catch (error) {
      setNotice(errorMessage(error));
    }
  }

  if (editor !== null) {
    const dirty =
      editor.description !== editor.file.description || editor.content !== editor.file.content;
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
        <p className="font-mono text-portal-micro break-all text-portal-muted">{editor.file.path}</p>
        {notice === null ? null : <ErrorNotice inline title="Could not save" message={notice} />}
        <label className="block">
          <span className="mb-1 block text-portal-micro font-medium text-portal-muted">
            What this memory holds
          </span>
          <input
            value={editor.description}
            onChange={(event) => setEditor({ ...editor, description: event.target.value })}
            maxLength={200}
            className="w-full rounded-md border border-admin-line px-2 py-1.5 text-portal-small focus:border-brand/60 focus:outline-none"
          />
        </label>
        <label className="flex min-h-0 flex-1 flex-col">
          <span className="mb-1 block text-portal-micro font-medium text-portal-muted">Content</span>
          <textarea
            value={editor.content}
            onChange={(event) => setEditor({ ...editor, content: event.target.value })}
            maxLength={8000}
            className="min-h-48 flex-1 resize-none rounded-md border border-admin-line p-2 font-mono text-portal-micro leading-relaxed focus:border-brand/60 focus:outline-none"
          />
        </label>
        <span className="flex shrink-0 items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setEditor(null);
              setNotice(null);
            }}
            className="cursor-pointer rounded-portal-button px-3 py-1.5 text-portal-small font-medium text-portal-muted hover:bg-portal-bg hover:text-portal-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!dirty || save.isPending || editor.content.trim().length === 0}
            onClick={() => save.mutate(editor)}
            className="cursor-pointer rounded-portal-button bg-brand px-3 py-1.5 text-portal-small font-medium text-white hover:bg-brand/90 disabled:cursor-default disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save memory'}
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-3">
      {notice === null ? null : (
        <div className="mb-2">
          <ErrorNotice inline title="Something went wrong" message={notice} />
        </div>
      )}
      {memories.isPending ? (
        <p className="p-4 text-portal-small text-portal-muted">Loading memories…</p>
      ) : memories.isError ? (
        <ErrorNotice inline title="Memory unavailable" message="Could not load memories." />
      ) : memories.data.length === 0 ? (
        <EmptyState
          icon={<BrainIcon aria-hidden className="size-6" />}
          title="Nothing remembered yet"
          message="As you chat, the assistant saves durable preferences and facts here — and you can edit or remove anything."
        />
      ) : (
        <div className="space-y-2">
          {memories.data.map((entry) => (
            <MemoryCard
              key={entry.path}
              entry={entry}
              onOpen={() => void openEditor(entry)}
              onDelete={() => setDeleting(entry)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleting !== null}
        tone="danger"
        title="Forget this memory?"
        message={deleting ? `"${deleting.description}" will be permanently removed.` : ''}
        confirmLabel="Forget it"
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.path);
        }}
        onCancel={() => {
          if (!remove.isPending) setDeleting(null);
        }}
        pending={remove.isPending}
      />
    </div>
  );
}

/**
 * One memory file as a card: the description up top, the path and last-write
 * time beneath, hover actions to edit or forget.
 *
 * @param props - The entry and its action handlers.
 * @returns The memory card.
 */
function MemoryCard({
  entry,
  onOpen,
  onDelete,
}: {
  readonly entry: MemoryIndexEntry;
  readonly onOpen: () => void;
  readonly onDelete: () => void;
}): React.ReactElement {
  const when = whenLabel(entry.updatedAt);
  return (
    <div className="group relative rounded-lg border border-admin-line bg-white p-3 shadow-sm transition-shadow hover:shadow-md">
      <button type="button" onClick={onOpen} className="block w-full cursor-pointer pr-12 text-left">
        <span className="block truncate text-portal-small font-bold text-portal-ink">
          {entry.description}
        </span>
        <span className="mt-0.5 block truncate font-mono text-portal-micro text-admin-muted">
          {entry.path}
          {when ? ` · ${when}` : ''}
        </span>
      </button>
      <span className="absolute top-2 right-2 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Edit memory"
              onClick={onOpen}
              className="grid size-6 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"
            >
              <PencilIcon aria-hidden className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Edit</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Forget memory"
              onClick={onDelete}
              className="grid size-6 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-red-50 hover:text-red-600"
            >
              <XIcon aria-hidden className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Forget</TooltipContent>
        </Tooltip>
      </span>
    </div>
  );
}
